import React from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, Scale, AlertTriangle, ExternalLink, Flag } from "lucide-react";
import SubmitRegistryTip from "../components/SubmitRegistryTip";
import CoverageMap from "../components/CoverageMap";
import SEO from "../components/SEO";

const JUSTICE_IMG =
  "https://images.unsplash.com/photo-1750727125196-c11918763096?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwyfHxjb3VydGhvdXNlJTIwc2NhbGVzJTIwb2YlMjBqdXN0aWNlfGVufDB8fHx8MTc3NjYzMzA5OHww&ixlib=rb-4.1.0&q=85";

export default function About() {
  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12" data-testid="about-page">
      <SEO
        title="About · How We Verify Animal Abuse Convictions"
        description="How Paws on Record sources, verifies and publishes convicted animal abuser records — TBI registry, county registries, MSU Animal Legal & Historical Center appellate cases, and verified court convictions."
        path="/about"
      />
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-7">
          <div className="label-eyebrow">Section · 04 · About</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter mt-2">
            Transparency
            <br />
            protects animals.
          </h1>
          <p className="text-lg text-zinc-700 mt-6 leading-relaxed">
            Animal cruelty is a felony in all 50 U.S. states. Convictions are
            recorded across thousands of county courts and a small number of
            official registries — leaving shelters, rescues and fostering
            families without a single place to check prospective adopters.
          </p>
          <p className="text-lg text-zinc-700 mt-4 leading-relaxed">
            <strong>Paws on Record</strong> aggregates convictions from public
            government registries (Tennessee TBI, Henrico County VA, Suffolk
            County NY), documented appellate records at the{" "}
            <a
              href="https://www.animallaw.info/"
              target="_blank"
              rel="noreferrer"
              className="underline"
            >
              Michigan State University Animal Legal & Historical Center
            </a>{" "}
            (Texas, California, Florida and more), and publicly reported news
            coverage — each record linked to its source so you can verify
            independently.
          </p>

          <div className="mt-10 space-y-5">
            <Card
              icon={<ShieldCheck size={22} />}
              title="Where the data comes from"
              body="Every record is sourced from a public government registry or a published news account of a finalized conviction. Each entry includes a source URL you can verify independently."
            />
            <Card
              icon={<Scale size={22} />}
              title="What this tool is not"
              body="This is not an official government source and is not a legal background check. Use the linked sources to confirm details; official state/county registries remain the authoritative records."
              danger
            />
            <Card
              icon={<AlertTriangle size={22} />}
              title="Reporting abuse"
              body="If an animal is in immediate danger, call 911. For non-emergency reports, use our Report form or contact your local animal control agency."
            />
          </div>

          <div className="mt-10 border border-zinc-900 p-6 bg-zinc-50">
            <div className="label-eyebrow mb-3">Official Resources</div>
            <ul className="space-y-2">
              {[
                { label: "ASPCA — Report Animal Cruelty", url: "https://www.aspca.org/pet-care/report-animal-cruelty" },
                { label: "Humane Society of the United States", url: "https://www.humanesociety.org/resources/report-animal-cruelty" },
                { label: "Tennessee Animal Abuse Registry (official)", url: "https://www.tn.gov/tbi/general-information/tennessee-animal-abuse-registry.html" },
                { label: "FBI — Animal Cruelty Statistics (NIBRS)", url: "https://www.fbi.gov/news/stories/-tracking-animal-cruelty" },
              ].map((r) => (
                <li key={r.url}>
                  <a href={r.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 font-semibold hover:underline">
                    {r.label} <ExternalLink size={12} />
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div className="mt-8 border-2 border-zinc-900 bg-white p-6" data-testid="about-contact-block">
            <div className="label-eyebrow text-red-600">Contact</div>
            <div className="font-display font-black text-2xl tracking-tight mt-1">
              Corrections · Takedowns · Press · Partnerships
            </div>
            <p className="text-sm text-zinc-700 mt-3 max-w-2xl leading-relaxed">
              If you&apos;re a listed individual disputing a record, a court clerk with an
              updated ruling, a shelter partner, or a reporter — reach us directly.
              We respond within 48 hours.
            </p>
            <a
              href="mailto:info@pawsonrecord.com?subject=Paws%20on%20Record%20Inquiry"
              className="mt-4 inline-flex items-center gap-2 bg-zinc-900 text-white hover:bg-red-600 border-2 border-zinc-900 hover:border-red-600 px-5 py-3 text-sm font-bold uppercase tracking-widest transition-colors"
              data-testid="about-contact-email"
            >
              info@pawsonrecord.com
            </a>
          </div>

          <div className="mt-10 flex flex-wrap gap-4">
            <Link to="/" className="btn-primary" data-testid="about-registry-cta">
              Explore the Registry
            </Link>
            <Link to="/report" className="btn-danger" data-testid="about-report-cta">
              Report Abuse
            </Link>
          </div>
        </div>

        <aside className="lg:col-span-5">
          <div className="sticky top-24 space-y-6">
            <div className="border border-zinc-900">
              <img
                src={JUSTICE_IMG}
                alt="Courthouse building at sunset"
                className="w-full h-80 object-cover grayscale contrast-125"
              />
              <div className="p-5 border-t border-zinc-900">
                <div className="label-eyebrow">Legal disclaimer</div>
                <p className="text-sm text-zinc-700 mt-2 leading-relaxed">
                  Paws on Record aggregates publicly available conviction
                  records. While we link each entry to its official source,
                  this tool is not a substitute for a formal background check.
                  Confirm details with the authoritative government source
                  before making any legal, employment, housing, or adoption
                  decision.
                </p>
              </div>
            </div>

            <div className="border border-red-600 bg-red-50 p-5">
              <div className="label-eyebrow text-red-700">Emergency</div>
              <div className="font-display font-black text-3xl mt-1">911</div>
              <p className="text-sm mt-2 text-red-900">
                If an animal is in immediate danger or you witness abuse in
                progress, call 911.
              </p>
            </div>
          </div>
        </aside>
      </div>

      <section id="submit-tip" className="mt-16 border-t-2 border-zinc-900 pt-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="label-eyebrow flex items-center gap-2">
              <Flag size={12} className="text-red-600" /> Community tip
            </div>
            <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tighter mt-2">
              Know of a conviction
              <br />
              that's not in here?
            </h2>
            <p className="text-zinc-700 mt-4 leading-relaxed text-base">
              Most animal-cruelty convictions never make national news, and the
              public registries we pull from (Tennessee TBI, Suffolk NY SPCA,
              Henrico VA) cover only a fraction of the country. If you've seen
              a conviction reported locally, paste the source here — a human
              reviewer will verify and add it.
            </p>
            <p className="text-sm text-zinc-500 mt-4 leading-relaxed">
              All tips are rate-limited by IP and require a source link. We do
              not add unverified allegations — only convictions or guilty pleas
              documented in a court filing or major news source.
            </p>
          </div>
          <div className="lg:col-span-7">
            <SubmitRegistryTip />
          </div>
        </div>
      </section>

      <CoverageMap />
    </div>
  );
}

function Card({ icon, title, body, danger }) {
  return (
    <div className={`border p-5 ${danger ? "border-red-600 bg-red-50" : "border-zinc-300 bg-white"}`}>
      <div className="flex items-start gap-4">
        <div className={`w-10 h-10 flex items-center justify-center ${danger ? "bg-red-600 text-white" : "bg-zinc-900 text-white"}`}>
          {icon}
        </div>
        <div>
          <div className="font-display font-black text-xl tracking-tight">{title}</div>
          <div className="text-sm text-zinc-700 mt-1 leading-relaxed">{body}</div>
        </div>
      </div>
    </div>
  );
}
